import React, { useCallback } from 'react';
import { Formik, Form } from 'formik';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import PageLoader from 'components/PageLoader';
import { isIOS, Option } from 'components/Option/Option';
import { subscriptions } from '../../constants';
import styles from './../EditClient/styles';
import Divider from '@material-ui/core/Divider';
import ClosesIcon from 'assets/images/close-icon.svg';
import cn from 'classnames';
import AddIcon from '@material-ui/icons/Add';
import { Tooltip } from './components/Tooltip';
import { TOOLTIP_TEXT } from './components/tooltips-text';
import { REGION_TYPE, DATE_FORMAT_CONFIG, TIME_FORMAT } from 'common/dateFormatConfig';
import CompanyDateFormatBlock from './components/CompanyDateFormatBlock/CompanyDateFormatBlock';

const validation = (values) => {
  const requiredField = "This field can't be blank";
  const senderEmail = 'Domain name is incorrect, please use @3208tech.com';
  const errors = {};
  if (!values.name.trim()) {
    errors.name = requiredField;
  }

  if (!values.settings?.emailSender?.trim()) {
    errors.emailSender = requiredField;
  }

  if (values.settings?.emailSender?.trim()) {
    if (
      !values.settings.emailSender.match(/.*(@3208tech\.com)$|^(materialcerts@guidemark\.info)$/)
    ) {
      errors.emailSender = senderEmail;
    }
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.settings.emailSender)) {
      errors.emailSender = 'Invalid email address';
    }
    if (values.settings.emailSender > 50) {
      errors.emailSender = 'The field cannot contain more than 50 symbols. Please, check';
    }
    if (!/^\S*$/.test(values.settings.emailSender)) {
      errors.emailSender = 'No spaces allowed';
    }
  }
  return errors;
};

const AddClientForm = ({ classes, closeDrawer, addClient }) => {
  const handleNotificationChange = useCallback(
    (i, setFieldValue, fieldName) => (e) => {
      const { value } = e.target;
      setFieldValue(`settings.emailReceivers[${i}][${fieldName}]`, value.trim());
    },
    []
  );
  const handleEmailRemoving = (values, i, setFieldValue) => () => {
    const nextValue = values.settings.emailReceivers.filter((_, index) => index !== i);
    setFieldValue('settings.emailReceivers', nextValue);
  };
  const handleAddEmail = (values, setFieldValue) => () => {
    setFieldValue('settings.emailReceivers', [
      ...values?.settings?.emailReceivers.filter((notificationObject) => notificationObject.email),
      { name: '', email: '' },
    ]);
  };

  // if (!client) return '';

  return (
    <div className={classes.formWrapper} role="presentation">
      <Formik
        validate={validation}
        validateOnChange={false}
        validateOnBlur={true}
        initialValues={{
          name: '',
          subscription: subscriptions[0],
          settings: {
            emailReceivers: [],
            emailSender: 'materialcerts@guidemark.info',
            dateFormatType: REGION_TYPE[REGION_TYPE.us],
            dateFormat: DATE_FORMAT_CONFIG[REGION_TYPE.us][0],
            timeFormat: TIME_FORMAT[0],
          },
          weekStart: 'Sun',
        }}
        enableReinitialize={true}
        onSubmit={(values) => {
          let filteredData = null;
          if (values?.settings?.emailReceivers) {
            filteredData = values.settings.emailReceivers.filter((receiver) => !!receiver.email);
          } else {
            filteredData = [];
          }

          addClient({
            ...values,
            settings: {
              ...values.settings,
              emailReceivers: filteredData,
              weekStart: values.weekStart,
              generateCertificates: false,
            },
          });
        }}
      >
        {({ values, handleChange, errors, touched, handleBlur, setFieldValue }) => (
          <PageLoader loading={false}>
            <Form autoComplete="off" className={classes.formFlex}>
              <div>
                <Typography variant="h3" className={classes.marginHeader}>
                  Add New Company
                </Typography>

                <IconButton
                  className={classes.closeButton}
                  onClick={closeDrawer}
                  aria-label="Close"
                  disableTouchRipple
                >
                  <CloseIcon />
                </IconButton>

                <Grid container spacing={2} className={classes.flexContainer}>
                  <Grid item xs={12} lg={6} className={classes.fieldWrapper}>
                    <label htmlFor="name" className={classes.label}>
                      <Typography variant="body1" color="textSecondary">
                        Company name:
                      </Typography>
                      <TextField
                        id="name"
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={classes.textField}
                        error={Boolean(errors.name) && Boolean(touched.name)}
                        variant="outlined"
                        inputProps={{ 'aria-label': 'name' }}
                        fullWidth
                        placeholder="Enter name"
                        SelectProps={{
                          native: isIOS,
                        }}
                      />
                      {errors.name && touched.name ? (
                        <div className={classes.error}>{errors.name}</div>
                      ) : null}
                    </label>
                  </Grid>
                  <Grid item xs={12} lg={6} className={classes.fieldWrapper}>
                    <label htmlFor="subscription" className={classes.label}>
                      <Typography variant="body1" color="textSecondary">
                        Subscription:
                      </Typography>
                      <TextField
                        select
                        id="subscription"
                        name="subscription"
                        value={values.subscription}
                        onChange={handleChange}
                        className={classes.textField}
                        variant="outlined"
                        inputProps={{ 'aria-label': 'Subscription' }}
                        fullWidth
                        SelectProps={{
                          native: isIOS,
                        }}
                      >
                        {subscriptions.map((el) => (
                          <Option key={el} value={el}>
                            {el}
                          </Option>
                        ))}
                      </TextField>
                    </label>
                  </Grid>
                  <Grid item xs={12} lg={6} className={classes.fieldWrapper}>
                    <label htmlFor="weekStart" className={classes.label}>
                      <Typography variant="body1" color="textSecondary">
                        Week Shift:
                      </Typography>
                      <TextField
                        select
                        id="weekStart"
                        name="weekStart"
                        value={values.weekStart}
                        onChange={handleChange}
                        className={classes.textField}
                        variant="outlined"
                        inputProps={{ 'aria-label': 'weekStart' }}
                        fullWidth
                        SelectProps={{
                          native: isIOS,
                        }}
                      >
                        <Option key={'Sun'} value={'Sun'}>
                          Sun - Sat
                        </Option>
                        <Option key={'Mon'} value={'Mon'}>
                          Mon - Sun
                        </Option>
                      </TextField>
                    </label>
                  </Grid>
                  <Grid item xs={12} lg={6} className={classes.fieldWrapper}>
                    <label htmlFor="generateCertificates" className={classes.label}>
                      <Typography variant="body1" color="textSecondary">
                        Material Certificates:
                      </Typography>
                      <TextField
                        select
                        id="settings.generateCertificates"
                        name="settings.generateCertificates"
                        value={values.settings.generateCertificates || false}
                        onChange={handleChange}
                        className={classes.textField}
                        variant="outlined"
                        inputProps={{ 'aria-label': 'generateCertificates' }}
                        fullWidth
                        SelectProps={{
                          native: isIOS,
                        }}
                      >
                        <Option key={'true'} value={true}>
                          Yes
                        </Option>
                        <Option key={'false'} value={false}>
                          No
                        </Option>
                      </TextField>
                    </label>
                  </Grid>
                </Grid>
                <Divider className={classes.marginVertical} />
                <Typography variant="h4" className={classes.marginSubHeader}>
                  Email Notifications:
                </Typography>
                <Grid container spacing={2} className={classes.flexContainer}>
                  <Grid item xs={12} lg={12}>
                    <label htmlFor="name" className={cn(classes.labelEmail, classes.label)}>
                      <Typography variant="body1" color="textSecondary">
                        Receiver:
                      </Typography>
                    </label>
                    <div className={classes.actionsWrapper}>
                      <Button
                        variant="text"
                        className={classes.primaryTextButton}
                        onClick={handleAddEmail(values, setFieldValue)}
                        disableRipple
                      >
                        <AddIcon fontSize="small" /> Add receiver email
                      </Button>
                      {values.settings.emailReceivers.map((notificationObject, index) => (
                        <Grid
                          key={index}
                          container
                          spacing={1}
                          className={classes.emailBlockWrapper}
                        >
                          <Grid item xs={11} lg={5}>
                            {index === 0 && (
                              <Typography
                                variant="body1"
                                color="textSecondary"
                                className={classes.regularFontWeight}
                              >
                                Name:
                              </Typography>
                            )}
                            <TextField
                              id={`settings.emailReceivers[${index}].name`}
                              name={`settings.emailReceivers[${index}].name`}
                              type="text"
                              value={
                                values?.settings?.emailReceivers &&
                                values.settings?.emailReceivers[index]?.name
                              }
                              className={classes.textField}
                              onChange={handleNotificationChange(index, setFieldValue, 'name')}
                              variant="outlined"
                              inputProps={{
                                'aria-label': `settings.emailReceivers[${index}].name`,
                              }}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={11} lg={6}>
                            {index === 0 && (
                              <Typography
                                variant="body1"
                                color="textSecondary"
                                className={classes.regularFontWeight}
                              >
                                Email:
                              </Typography>
                            )}
                            <TextField
                              id={`settings.emailReceivers[${index}].email`}
                              name={`settings.emailReceivers[${index}].email`}
                              type="email"
                              value={
                                values?.settings?.emailReceivers &&
                                values.settings?.emailReceivers[index]?.email
                              }
                              className={classes.textField}
                              onChange={handleNotificationChange(index, setFieldValue, 'email')}
                              variant="outlined"
                              inputProps={{
                                'aria-label': `settings.emailReceivers[${index}].email`,
                              }}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={1} lg={1}>
                            <span
                              className={classes.removeEmail}
                              onClick={handleEmailRemoving(values, index, setFieldValue)}
                            >
                              <img src={ClosesIcon} alt="delete" />
                            </span>
                          </Grid>
                        </Grid>
                      ))}
                    </div>
                  </Grid>
                </Grid>
                <Grid container spacing={2} className={classes.flexContainer}>
                  <Grid item xs={12} lg={6} className={classes.fieldWrapper}>
                    <label htmlFor="name" className={classes.label}>
                      <Tooltip title={'Sender:'} text={TOOLTIP_TEXT.senderText} />
                    </label>
                    <Grid item xs={12} lg={12}>
                      <TextField
                        id={'settings.emailSender'}
                        name={'settings.emailSender'}
                        type="text"
                        value={values.settings?.emailSender || ''}
                        error={
                          Boolean(errors?.emailSender) && Boolean(touched?.settings?.emailSender)
                        }
                        onBlur={handleBlur}
                        className={classes.textField}
                        onChange={handleChange}
                        variant="outlined"
                        inputProps={{ 'aria-label': 'Sender' }}
                        fullWidth
                      />
                      {errors?.emailSender && touched?.settings?.emailSender ? (
                        <div className={classes.error}>{errors.emailSender}</div>
                      ) : null}
                    </Grid>
                  </Grid>
                </Grid>
                <Divider className={classes.marginVertical} />
                <Grid container spacing={2} className={classes.flexContainer}>
                  <CompanyDateFormatBlock
                    values={values}
                    classes={classes}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                  />
                </Grid>
              </div>
              <div className={classes.buttonHolder}>
                <Button color="secondary" className={classes.cancelButton} onClick={closeDrawer}>
                  Cancel
                </Button>
                <div className={classes.verticalDivider} />
                <Button type="submit" color="primary" className={classes.saveButton}>
                  Save
                </Button>
              </div>
            </Form>
          </PageLoader>
        )}
      </Formik>
    </div>
  );
};

export default withStyles(styles)(AddClientForm);
